import { PlusOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Select } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import {
  setCreateClientState,
  setCustomiseSectionState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
} from "../../slices/appStateSlice";
import { resetClientForm, setIsEditing } from "../../slices/clientSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import styles from "./home.module.scss";

const ClientSelector = () => {
  const componentName = "home";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobileView();

  const { rootPath } = useRootPath();

  const { referIndividualBy } = useSpecialityReference();

  const { clients } = useAppSelector((state) => state.client);
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { isDemoPath } = useAppSelector((state) => state.appState);

  const clientOptions = useMemo(() => {
    return clients.map((client) => {
      const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";
      const derivedAgeFromDob =
        client.date_of_birth && formattedDob ? dayjs().diff(dayjs(client.date_of_birth), "year") : null;
      const rawAge =
        typeof client.age === "number" ? client.age : client.age ? Number.parseInt(client.age.toString(), 10) : null;
      const displayAge = formattedDob ? derivedAgeFromDob : rawAge;
      const searchTokens = [client.name];

      if (client.mrn) {
        searchTokens.push(client.mrn);
      }
      if (formattedDob) {
        searchTokens.push(formattedDob);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        searchTokens.push(displayAge.toString());
      }

      const secondaryDetails: string[] = [];
      if (client.mrn) {
        secondaryDetails.push(`MRN: ${client.mrn}`);
      }
      if (formattedDob) {
        secondaryDetails.push(`DOB: ${formattedDob}`);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        secondaryDetails.push(`Age: ${displayAge}`);
      }

      return {
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Flex align="center" gap={5}>
              <span>
                {client.name}{" "}
                {!client.is_owned && client.owner_name && (
                  <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                    - Shared by {client.owner_name}
                  </span>
                )}{" "}
              </span>{" "}
            </Flex>
            {secondaryDetails.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                {secondaryDetails.join(" · ")}
              </span>
            )}
          </div>
        ),
        value: client.id,
        searchKey: searchTokens.join(" ").toLowerCase(),
      };
    });
  }, [clients]);

  const selectClient = (clientId: number) => {
    const client = clients.find((c) => c.id === clientId);
    const clientUuid = client?.uuid;
    navigate(`${rootPath}/clients/${clientUuid}`);
  };

  const clearSelection = () => {
    if (!isDemoPath) {
      dispatch(setLoadingNotesState(true));
    }
    navigate(rootPath);
  };

  const showClientView = () => {
    const filterClient = document.querySelector(".filterClient");
    if (filterClient) {
      filterClient.classList.add("ant-select-dropdown-hidden");
    }
    dispatch(setCreateClientState(true));
    dispatch(setIsEditing(false));
    dispatch(resetClientForm());
    dispatch(setSelectedClient(null));
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  return (
    <Flex gap={10}>
      <Select
        showSearch
        placeholder={`Select a ${referIndividualBy.toLowerCase()}`}
        onChange={selectClient}
        onClear={clearSelection}
        allowClear
        listHeight={isMobile ? 66 : 256}
        placement="bottomLeft"
        filterOption={(input, option) => {
          const normalizedInput = input.trim().toLowerCase();
          if (!normalizedInput) {
            return true;
          }
          if (!option) {
            return false;
          }
          const optionWithSearchKey = option as DefaultOptionType & { searchKey?: string };
          if (optionWithSearchKey.searchKey?.includes(normalizedInput)) {
            return true;
          }
          const { label } = optionWithSearchKey;
          const labelText = typeof label === "string" ? label : typeof label === "number" ? String(label) : "";
          if (labelText) {
            return labelText.toLowerCase().includes(normalizedInput);
          }
          return false;
        }}
        popupClassName="filterClient"
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            <Flex gap={10}>
              <Button type="text" icon={<PlusOutlined />} onClick={showClientView}>
                Add {referIndividualBy}
              </Button>
            </Flex>
          </div>
        )}
        style={{
          flexGrow: 1,
        }}
        options={clientOptions}
      />
      <Button
        className={styles[`${componentName}__container-mobile-button`]}
        type="primary"
        onClick={showClientView}
        icon={<PlusOutlined />}
      >
        Add {referIndividualBy}
      </Button>
    </Flex>
  );
};

export default ClientSelector;
